import { Text, View, ScrollView, Pressable, RefreshControl, useWindowDimensions, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Animated, { FadeInDown } from 'react-native-reanimated';
import ProfileCard from '@/components/People/ProfileCard';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import { LegendList } from "@legendapp/list";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { deleteRole } from '@/data/roles';
import Toast from 'react-native-toast-message';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Callout from '@/components/General/Callout';
import { showAlert } from '@/utils/showAlert';
import PageTitle from '@/components/General/PageTitle';
import ContentLoader from '@/components/General/ContentLoader';
import ActionButton from '@/components/General/ActionButton';
import { useRouter, useLocalSearchParams, useNavigation } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import usePlatform from '@/utils/hooks/usePlatform';
import React from 'react';
import { saveToRecents } from '@/utils/recentlyVisited';
import SearchBox from '@/components/General/inputs/SearchBox';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';
import RoleResourcesList, { getRoleResources } from '@/components/Roles/RoleResourcesList';

// ============ INFO PANEL ============
const RoleInfoPanel = React.memo(({ roleID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: role, isFetching } = Query.roles.byId({ id: roleID });

    // Track recently visited
    useEffect(() => {
        if (role?.id) {
            saveToRecents({
                id: role.id,
                url: `/roles/${role.id}`,
                title: role.name,
                params: { roleID: role.id },
            });
        }
    }, [role?.id]);
    const { data: quickFilterMembers } = Query.people.all({
        filters: role?.QuickFilter?.filterJSON,
        filterLogic: role?.QuickFilter?.filterLogic,
        options: { enabled: (!isFetching && !!role?.quickFilterID) }
    });

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.roles.byId(roleID) });

    const memberCount = role?.quickFilterID
        ? (quickFilterMembers?.pages?.flatMap(m => m.data)?.length || 0)
        : (role?.People?.length || 0);
    const permissionCount = role?.Permissions?.length || 0;
    const resourceCount = getRoleResources(role).length;
    const canManageNotAdmin = loggedInUser?.ManagingRoles?.some(r => r.id === role?.id) && !loggedInUser?.isAdmin;

    return (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh}/>}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <Animated.View entering={FadeInDown.duration(400)} style={styles.headerCard}>
                    {/* Hidden Badge */}
                    {role?.isHidden && (
                        <View style={styles.hiddenBadge}>
                            <Unicon name="eye-off" size={14} colorVariant="warning" />
                            <Text style={styles.hiddenBadgeText}>Hidden Role</Text>
                        </View>
                    )}

                    {/* Dynamic Badge */}
                    {role?.quickFilterID && (
                        <View style={styles.dynamicBadge}>
                            <Unicon library="fa5" name="user-check" size={14} colorVariant="info" />
                            <Text style={styles.dynamicBadgeText}>Dynamic Role</Text>
                        </View>
                    )}

                    {/* Title Row */}
                    <View style={styles.titleRow}>
                        <View style={styles.iconBadge}>
                            <Unicon name="shield-checkmark" size={24} colorVariant="primary" />
                        </View>
                        <View style={styles.titleInfo}>
                            <Text style={styles.roleTitle}>{role?.name}</Text>
                            {!!role?.description && <Text style={styles.roleSubtitle}>{role.description}</Text>}
                        </View>
                    </View>

                    {/* Stats Row */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Members</Text>
                            <Text style={styles.statValue}>{memberCount}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Permissions</Text>
                            <Text style={styles.statValue}>{permissionCount}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Resources</Text>
                            <Text style={styles.statValue}>{resourceCount}</Text>
                        </View>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        <ActionButton
                            iconName="create"
                            text={canManageNotAdmin ? "Edit Members" : "Edit"}
                            onPress={() => router.push({ pathname: "/roles/create", params: { id: role?.id, folderID: role?.folderID } })}
                        />
                    </View>
                </Animated.View>

                {/* Dynamic Role Callout */}
                {!!role?.quickFilterID && (
                    <Animated.View entering={FadeInDown.delay(150).duration(400)}>
                        <Callout type="info" message="This role is dynamic. Members are determined by a filter and will change automatically." />
                    </Animated.View>
                )}

                {/* Resources Section */}
                {resourceCount > 0 && (
                    <Animated.View entering={FadeInDown.delay(300).duration(400)} style={styles.infoSection}>
                        <Text style={styles.sectionLabel}>Resources</Text>
                        <RoleResourcesList role={role} />
                    </Animated.View>
                )}
            </ScrollView>
        </View>
    );
});

// ============ MEMBERS TAB ============
const MembersTab = React.memo(({ roleID }) => {
    const queryClient = useQueryClient();
    const { data: role, isFetching } = Query.roles.byId({ id: roleID });
    const { data: quickFilterMembers, hasNextPage, fetchNextPage } = Query.people.all({
        filters: role?.QuickFilter?.filterJSON,
        filterLogic: role?.QuickFilter?.filterLogic,
        options: { enabled: (!isFetching && !!role?.quickFilterID) }
    });

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.roles.byId(roleID) });
    const members = role?.quickFilterID ? (quickFilterMembers?.pages?.flatMap(m => m.data) || []) : (role?.People || []);

    return (
        <View style={styles.tabContent}>
            <LegendList
                recycleItems={Platform.OS !== 'web'}
                keyExtractor={(item) => item.id}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={60}
                data={isFetching ? [] : members}
                onEndReached={() => (!isFetching && hasNextPage) && fetchNextPage()}
                renderItem={({ item }) => (
                    <View>
                        {item.canManage && (
                            <View style={styles.managerBadge}>
                                <Unicon name="star" size={10} colorVariant="info" />
                            </View>
                        )}
                        <ProfileCard minimal key={item.id} profile={item} />
                    </View>
                )}
                contentContainerStyle={styles.listPadding}
                refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
                ListHeaderComponent={!!role?.quickFilterID && (
                    <View style={{ padding: 10 }}>
                        <Callout type="info" message="This role is dynamic, so the members listed will change based on the selected filter." />
                    </View>
                )}
                ListFooterComponent={isFetching
                    ? <ContentLoader containerHeight={60} spacingVertical={8} amount={6} />
                    : members.length === 0
                        ? <EmptyListPlaceholder icon="people" spacing={20} message="This role currently doesn't have any members" />
                        : <View style={{ paddingBottom: 80 }} />
                }
/>
        </View>
    );
});

// ============ PERMISSIONS TAB ============
const PermissionsTab = React.memo(({ roleID }) => {
    const router = useRouter();
    const { data: role, isFetching } = Query.roles.byId({ id: roleID });
    const [searchQuery, setSearchQuery] = useState('');

    const permissionsByModule = useMemo(() => {
        const map = {};
        role?.Permissions?.forEach(p => {
            if (!map[p.module]) map[p.module] = [];
            map[p.module].push(p);
        });
        return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }, [role?.Permissions]);

    const filteredPermissionsByModule = useMemo(() => {
        if (!searchQuery) return permissionsByModule;
        const lowerQuery = searchQuery.toLowerCase();
        return permissionsByModule.map(([moduleName, modulePermissions]) => {
            const filtered = modulePermissions.filter(p =>
                p.name?.toLowerCase().includes(lowerQuery) ||
                p.description?.toLowerCase().includes(lowerQuery) ||
                moduleName.toLowerCase().includes(lowerQuery)
            );
            return [moduleName, filtered];
        }).filter(([_, perms]) => perms.length > 0);
    }, [permissionsByModule, searchQuery]);

    return (
        <View style={styles.tabContent}>
            {isFetching ? (
                <ContentLoader containerHeight={70} spacingVertical={8} amount={6} />
            ) : permissionsByModule.length === 0 ? (
                <View style={styles.listPadding}>
                    <EmptyListPlaceholder icon="key" spacing={20} message="This role currently doesn't have any permissions" />
                </View>
            ) : (
                <ScrollView
                    style={styles.tabContent}
                    contentContainerStyle={styles.listPadding}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    {/* Search Input */}
                    <View style={{ marginBottom: 16 }}>
                        <SearchBox
                            searchText={searchQuery}
                            placeholder="Search permissions..."
                            onChangeText={setSearchQuery}
                            textChangeDelay={0}
                        />
                    </View>

                    {filteredPermissionsByModule.length === 0 && searchQuery ? (
                        <View style={styles.emptySearchContainer}>
                            <Text style={styles.emptySearchText}>No permissions match &ldquo;{searchQuery}&rdquo;</Text>
                        </View>
                    ) : (
                        filteredPermissionsByModule.map(([moduleName, modulePermissions]) => (
                            <View key={moduleName} style={styles.permissionsModuleSection}>
                                <Text style={styles.permissionsModuleHeader}>{moduleName}</Text>
                                {modulePermissions.map(perm => (
                                    <Pressable
                                        key={perm.id}
                                        style={styles.permissionItem}
                                        onPress={() => router.push({ pathname: "/permissions/" + perm?.id })}
                                    >
                                        <View style={styles.permissionIconWrap}>
                                            <Unicon name="key" size={18} colorVariant={perm.locked ? "warning" : "secondary"} />
                                        </View>
                                        <View style={styles.permissionInfo}>
                                            <View style={styles.permissionNameRow}>
                                                <Text style={styles.permissionName} numberOfLines={1}>{perm?.name}</Text>
                                                {perm.locked && <Unicon name="lock-closed" size={12} colorVariant="warning" />}
                                            </View>
                                            {!!perm?.description && <Text style={styles.permissionDesc} numberOfLines={2}>{perm.description}</Text>}
                                        </View>
                                        <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                                    </Pressable>
                                ))}
                            </View>
                        ))
                    )}
                    <View style={{ paddingBottom: 80 }} />
                </ScrollView>
            )}
        </View>
    );
});

// ============ MAIN SCREEN ============
function RoleScreenInner() {
    const [mobileTab, setMobileTab] = useState(0);
    const [activityTab, setActivityTab] = useState(0);

    const { isNative } = usePlatform();
    const navigation = useNavigation();
    const router = useRouter();
    const { roleID } = useLocalSearchParams();
    const { width } = useWindowDimensions();
    const isWideScreen = width >= 900;

    if (!roleID) {
        router.back();
    }

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const saveShortcut = useSaveShortcut(loggedInUser?.id);
    const { data: role, isFetching: fetchingRole } = Query.roles.byId({ id: roleID });

    const deleteRoleMutation = useMutation({
        mutationFn: deleteRole,
        onSuccess: () => {
            Toast.delayShow({ type: 'success', text2: `The Role "${role?.name}" has been deleted successfully! Note that users that have an active session may still have access to this role until they reload.` });
            queryClient.invalidateQueries({ queryKey: QueryKeys.roles.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.boards.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.calendars.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.folders.all() });
            router.back();
        }
    });

    useEffect(() => {
        if (!fetchingRole && !role) router.back();
    }, [fetchingRole]);

    useEffect(() => {
        const canManageNotAdmin = loggedInUser?.ManagingRoles?.some(r => r.id === role?.id) && !loggedInUser?.isAdmin;
        const options = [];

        if (role?.id) {
            options.push({
                name: "Save to Shortcuts",
                icon: dropdownIcons.favorite,
                onSelect: () => saveShortcut({ screen: `/roles/${role.id}`, label: role?.name || 'Role', icon: 'shield-outline' })
            });
        }

        options.push({
            name: canManageNotAdmin ? "Edit Members" : "Edit Role",
            icon: dropdownIcons.edit,
            onSelect: () => router.push({ pathname: "/roles/create", params: { id: role?.id, folderID: role?.folderID } })
        });

        if (loggedInUser?.isAdmin || (!role?.isAccessControl && loggedInUser?.can('Delete Roles'))) {
            options.push({
                name: "Delete Role",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: "Delete Role",
                    subtitle: "Are you sure you want to delete this Role? This may cause users to lose permissions and access to certain resources within the app.",
                    isDestructive: true,
                    onConfirm: () => deleteRoleMutation.mutate({ roleID: role?.id }),
                    confirmText: "Delete"
                })
            });
        }

        navigation.setOptions({
            headerTitle: () => <PageTitle title={role?.name} subtitle="Role" />,
            headerRight: () => <DropdownMenu testID="HeaderMenu" options={options} />,
        });
    }, [navigation, role, loggedInUser, saveShortcut]);

    // ============ TAB ROUTES ============
    const mobileRoutes = useMemo(() => {
        const r = [{ key: 'info', title: 'Info' }, { key: 'members', title: 'Members' }];
        if (loggedInUser?.isAdmin) r.push({ key: 'permissions', title: 'Permissions' });
        return r;
    }, [loggedInUser?.isAdmin]);

    const activityRoutes = useMemo(() => {
        const r = [{ key: 'members', title: 'Members' }];
        if (loggedInUser?.isAdmin) r.push({ key: 'permissions', title: 'Permissions' });
        return r;
    }, [loggedInUser?.isAdmin]);

    useEffect(() => {
        if (mobileTab >= mobileRoutes.length) setMobileTab(0);
        if (activityTab >= activityRoutes.length) setActivityTab(0);
    }, [mobileTab, activityTab, mobileRoutes.length, activityRoutes.length]);

    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderActivityScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'members': return <MembersTab roleID={roleID} />;
            case 'permissions': return <PermissionsTab roleID={roleID} />;
            default: return null;
        }
    }, [roleID]);

    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'info') return <RoleInfoPanel roleID={roleID} />;
        return renderActivityScene({ route });
    }, [roleID, renderActivityScene]);

    // ============ LOADING ============
    if (!role?.id && fetchingRole) {
        return (
            <Animated.View entering={FadeInDown.duration(300)} style={styles.loadingContainer}>
                <View style={styles.loadingHeaderRow}>
                    <ContentLoader containerHeight={52} containerWidth={52} borderRadius={10} spacingVertical={0} />
                    <View style={styles.loadingHeaderInfo}>
                        <ContentLoader containerHeight={22} containerWidth="70%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                        <ContentLoader containerHeight={14} containerWidth="40%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                    </View>
                </View>
                <View style={styles.loadingStatsRow}>
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                </View>
                <ContentLoader containerHeight={180} borderRadius={12} staggerPosition={4} />
            </Animated.View>
        );
    }

    if (!role?.id && !fetchingRole) return <EmptyListPlaceholder type="404" message="Role not found" />;

    // ============ WIDE SCREEN ============
    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    <RoleInfoPanel roleID={roleID} />
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: Math.min(activityTab, activityRoutes.length - 1), routes: activityRoutes }}
                            renderScene={renderActivityScene}
                            onIndexChange={setActivityTab}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>
            </View>
        );
    }

    // ============ MOBILE ============
    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: Math.min(mobileTab, mobileRoutes.length - 1), routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={setMobileTab}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />
        </View>
    );
}

const styles = StyleSheet.create((theme, rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },

    // Loading State
    loadingContainer: { flex: 1, padding: 20, maxWidth: 500, width: rt.isLandscape ? '50%' : '100%' },
    loadingHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 20 },
    loadingHeaderInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
    loadingStatsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingStatItem: { flex: 1 },

    // Two Column Layout
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },

    // Header Card
    headerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 16, padding: 20, marginBottom: 16,
        shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3,
    },

    // Badges
    hiddenBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
        backgroundColor: theme.colors.warningContainer, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, marginBottom: 12,
    },
    hiddenBadgeText: { color: theme.colors.warning, fontSize: 12, fontFamily: theme.fonts.bold },
    dynamicBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
        backgroundColor: theme.colors.onInfo, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, marginBottom: 12,
    },
    dynamicBadgeText: { color: theme.colors.info, fontSize: 12, fontFamily: theme.fonts.bold },

    // Title Row
    titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    iconBadge: {
        width: 52, height: 52, backgroundColor: theme.colors.primaryContainer,
        borderRadius: 10, alignItems: 'center', justifyContent: 'center',
    },
    titleInfo: { flex: 1, marginLeft: 14 },
    roleTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    roleSubtitle: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 },

    // Stats Row
    statsRow: {
        flexDirection: 'row', alignItems: 'center', paddingVertical: 14,
        borderTopWidth: 1, borderBottomWidth: 1, borderColor: theme.colors.border, marginBottom: 16,
    },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

    // Info Section
    infoSection: {
        backgroundColor: theme.colors.surface, borderRadius: 16, padding: 16, marginBottom: 16,
        shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2,
    },
    sectionLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 12 },

    // Info Row
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 140 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    linkText: { fontSize: 14, color: theme.colors.primary, textAlign: 'right' },
    emptyText: { fontSize: 14, color: theme.colors.textSecondary, fontStyle: 'italic', textAlign: 'right' },

    // Members Preview
    membersAvatarRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    moreBadge: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.colors.border, alignItems: 'center', justifyContent: 'center' },
    moreBadgeText: { fontSize: 11, color: theme.colors.textSecondary, fontFamily: theme.fonts.bold },

    // Tab Content
    tabContent: { flex: 1, padding: rt.isLandscape ? 10 : 0 },
    listPadding: { padding: 16, paddingBottom: 100 },

    // Manager Badge
    managerBadge: { position: "absolute", top: 10, left: 6, zIndex: 10 },

    // Permission Item
    permissionItem: {
        flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14,
        backgroundColor: theme.colors.surface, borderRadius: 12, marginBottom: 8,
        shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2,
        _web: { transition: 'all 0.15s ease-in-out', _hover: { backgroundColor: theme.colors.hover } },
    },
    permissionIconWrap: {
        width: 36, height: 36, borderRadius: 8, backgroundColor: theme.colors.secondaryContainer,
        alignItems: 'center', justifyContent: 'center',
    },
    permissionInfo: { flex: 1 },
    permissionNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    permissionName: { fontSize: 14, fontFamily: theme.fonts.heading, color: theme.colors.text },
    permissionDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },

    // Module Section
    moduleSection: {
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.border,
        overflow: 'hidden',
        backgroundColor: theme.colors.surface,
        marginBottom: 12,
    },
    moduleHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
        paddingHorizontal: 14,
        backgroundColor: theme.colors.surfaceVariant,
    },
    moduleName: {
        fontSize: 14,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    moduleBody: {
        paddingHorizontal: 14,
        paddingBottom: 10,
        paddingTop: 8,
    },

    // Permissions Module Section (like people page)
    permissionsModuleSection: { marginBottom: 20 },
    permissionsModuleHeader: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },

    // Empty Search
    emptySearchContainer: {
        paddingVertical: 20,
        alignItems: 'center',
    },
    emptySearchText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
}));

// Plan gate wraps the screen so paid-only role queries never mount on a
// deep link under the Free plan.
function RoleScreen() {
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <RoleScreenInner />;
}

export default RoleScreen;